import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, FileSignature, Camera, Wallet, Search, ShieldCheck } from "lucide-react";
import Header from "../components/Header";
import PageHero from "../components/PageHero";
import SocialProofBar from "../components/SocialProofBar";
import FeatureCardGrid from "../components/FeatureCardGrid";
import Footer from "../components/Footer";
import { useI18n } from "../lib/i18n";
import { CITY_AGENTS, CITIES, initials } from "../lib/agents";

export default function ForLandlords() {
  const { t } = useI18n();
  const [city, setCity]             = useState("Bangalore");
  const [citySearch, setCitySearch]   = useState("Bangalore");
  const [showCities, setShowCities]   = useState(false);

  const features = [
    {
      icon: "BadgeCheck",
      title: t("verified_tenants"),
      description: "Every tenant is Aadhaar-verified before they get the keys. Not a photo, not a forwarded ID — actual KYC.",
    },
    {
      icon: "FileSignature",
      title: t("court_valid"),
      description: "E-stamped, digitally signed rental agreement. Legally valid anywhere in India. Downloadable anytime.",
    },
    {
      icon: "Camera",
      title: t("property_documented"),
      description: "Room-by-room condition report with photos, locked at move-in. Undeniable evidence if there's damage at move-out.",
    },
    {
      icon: "Wallet",
      title: t("clean_exits"),
      description: "Both sides settle deductions on the platform. No last-day arguments, no chasing on WhatsApp.",
    },
  ];

  const tiles = [
    {
      icon: "🪪",
      headline: "Aadhaar + PAN verified tenants only.",
      subtext: "Every tenant is KYC verified before they sign a single document. Real identity, real accountability.",
    },
    {
      icon: "📄",
      headline: "Court-valid agreement. Every time.",
      subtext: "E-stamped, digitally signed, legally valid under Indian IT Act 2000. Downloadable anytime.",
    },
    {
      icon: "📸",
      headline: "Property condition. Locked on day one.",
      subtext: "Room-by-room photos sealed at move-in. If there's damage at move-out, the evidence already exists.",
    },
  ];

  return (
    <div style={{ background: "#080808", color: "#F5F5F5", minHeight: "100vh" }}>
      <Header />

      {/* Hero */}
      <PageHero
        eyebrow={t("nav_for_landlords")}
        title={t("rent_with_confidence")}
        subtitle="Most landlords lose money not from bad tenants — but from no documentation. Orello gives you Aadhaar-verified tenants, court-valid agreements, and timestamped room-by-room evidence before a single key is handed over."
        cta1Text="List Your Property"
        cta1Link="/register"
        cta2Text="Learn More"
        cta2Link="#"
        hasGlow={true}
        gradientWord="confidence"
      />

      {/* Value tiles */}
      <SocialProofBar tiles={tiles} />

      {/* Features */}
      <section style={{ background: "#0B0B14", padding: "4rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2
            className="font-display font-bold mb-16 text-center"
            style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Complete Tenant & Property Protection
          </h2>
          <FeatureCardGrid features={features} />
        </div>
      </section>

      {/* Why Landlords Choose Us */}
      <section style={{ background: "#111111", padding: "4rem 0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            {/* 10-day dispute card — replaces 99.2% */}
            <div style={{
              background: "linear-gradient(135deg, rgba(249,115,22,0.08), rgba(139,92,246,0.08))",
              borderRadius: 20,
              padding: "2.5rem",
              border: "1px solid rgba(249,115,22,0.15)",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}>
              <div style={{
                fontSize: "clamp(2.5rem, 5vw, 4rem)",
                fontWeight: 700,
                background: "linear-gradient(135deg, #F97316, #8B5CF6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                lineHeight: 1,
              }}>
                Instant.
              </div>
              <p style={{ fontSize: "1rem", fontWeight: 600, color: "#F5F5F5", margin: 0 }}>
                ORE-AI delivers an instant dispute verdict.
              </p>
              <p style={{ fontSize: "0.88rem", color: "#D1D5DB", lineHeight: 1.6, margin: 0 }}>
                India's first AI built for rental disputes. Reviews check-in photos, checkout condition, and local market rates — gives an instant binding decision.
              </p>
              <div style={{ borderTop: "1px solid rgba(245,245,245,0.08)", paddingTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  "Both sides submit evidence through the platform",
                  "ORE-AI analyses photos, reports & local market rates",
                  "Instant binding verdict. No lawyers, no courts.",
                ].map((point) => (
                  <div key={point} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#F97316", flexShrink: 0 }} />
                    <p style={{ fontSize: "0.82rem", color: "#9CA3AF", margin: 0 }}>{point}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-6">
                <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
                <p
                  className="text-xs tracking-widest uppercase"
                  style={{
                    background: "linear-gradient(135deg, #F97316, #A78BFA)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Why Landlords Choose Us
                </p>
              </div>
              <h3
                className="font-display font-bold mb-6"
                style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", color: "#F5F5F5" }}
              >
                Your property, documented and protected at every step.
              </h3>
              <p
                className="text-base font-light leading-relaxed mb-8"
                style={{ color: "#D1D5DB", maxWidth: 500 }}
              >
                From Aadhaar-verified tenants to room-by-room condition reports to ORE-AI dispute resolution — Orello removes every point of friction. You get clean tenancies and documented exits.
              </p>
              <Link
                to="/register"
                className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
                style={{
                  background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                  fontSize: "1rem",
                  boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
                }}
              >
                Get Started Free <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: "#080808", padding: "4rem 0" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="font-display font-bold leading-tight mb-6"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#F5F5F5" }}
          >
            Rent out your property.<br />
            <span style={{
              background: "linear-gradient(135deg, #F97316, #8B5CF6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              securely.
            </span>
          </h2>
          <p className="text-base font-light mb-12" style={{ color: "#D1D5DB" }}>
            No credit card. No setup fee. Ready in minutes.
          </p>
          <Link
            to="/register"
            className="btn-press inline-flex items-center gap-2 font-medium px-8 py-4 rounded-full text-white"
            style={{
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              fontSize: "1.05rem",
              boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
            }}
          >
            Create Free Account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Find Verified Agents — landlord trust table: 0 disputes, agreements, verified */}
      <section style={{ background: "#111111", padding: "5rem 0" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-4">
            <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #6366F1, #A78BFA)" }} />
            <p className="text-xs tracking-widest uppercase" style={{ color: "#A78BFA" }}>Verified Agents</p>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="font-display font-bold" style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)", color: "#F5F5F5", lineHeight: 1.2 }}>
                Find agents who protect<br />your property.
              </h2>
              <p className="mt-2" style={{ fontSize: "0.88rem", color: "#9CA3AF" }}>
                Every agent below uses Orello's full protection stack — agreement, KYC, deposit, condition report.
              </p>
            </div>
          </div>

          {/* Search bar */}
          <div className="relative mb-8">
            <Search size={16} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#6366F1" }} />
            <input
              type="text"
              placeholder="Search your city — Mumbai, Hyderabad, Chennai…"
              value={citySearch}
              onChange={e => setCitySearch(e.target.value)}
              onFocus={() => setShowCities(true)}
              onBlur={() => setTimeout(() => setShowCities(false), 150)}
              style={{
                width: "100%", padding: "13px 16px 13px 44px",
                background: "rgba(255,255,255,0.03)", border: "1px solid rgba(99,102,241,0.25)",
                borderRadius: 12, color: "#F5F5F5", fontSize: "0.9rem", outline: "none",
              }}
            />
            {showCities && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                background: "#111", border: "1px solid rgba(99,102,241,0.15)",
                borderRadius: 10, zIndex: 10, overflow: "hidden",
              }}>
                {CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase())).map(c => (
                  <button key={c} onMouseDown={() => { setCity(c); setCitySearch(c); setShowCities(false); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      padding: "10px 16px", fontSize: "0.88rem",
                      color: c === city ? "#A78BFA" : "#D1D5DB",
                      background: c === city ? "rgba(99,102,241,0.08)" : "transparent",
                      border: "none", cursor: "pointer",
                    }}>{c}</button>
                ))}
              </div>
            )}
          </div>

          {/* Clean verification list — landlord focus: deals closed, 0 disputes, shield badge */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {CITY_AGENTS[city].map((agent, i) => (
              <div key={agent.rank} style={{
                display: "flex", alignItems: "center", gap: 16,
                padding: "16px 20px", borderRadius: 12,
                background: i === 0 ? "rgba(99,102,241,0.07)" : "rgba(255,255,255,0.02)",
                border: `1px solid ${i === 0 ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.05)"}`,
              }}>
                {/* Rank */}
                <div style={{ width: 28, textAlign: "center", fontSize: "0.82rem", fontWeight: 800, color: i === 0 ? "#A78BFA" : "#4B5563", flexShrink: 0 }}>#{agent.rank}</div>

                {/* Avatar */}
                <div style={{
                  width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                  background: i === 0 ? "linear-gradient(135deg, #6366F1, #A78BFA)" : "rgba(255,255,255,0.06)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 700, fontSize: "0.75rem",
                }}>{initials(agent.name)}</div>

                {/* Name + area */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#F5F5F5" }}>{agent.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "#6B7280" }}>{agent.area}</div>
                </div>

                {/* Stats */}
                <div style={{ display: "flex", gap: 16, alignItems: "center", flexShrink: 0 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#F5F5F5" }}>{agent.deals}</div>
                    <div style={{ fontSize: "0.65rem", color: "#6B7280" }}>agreements</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#F97316" }}>★ {agent.rating}</div>
                    <div style={{ fontSize: "0.65rem", color: "#6B7280" }}>rating</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.88rem", fontWeight: 700, color: agent.disputes === 0 ? "#10B981" : "#EF4444" }}>
                      {agent.disputes === 0 ? "Zero" : agent.disputes}
                    </div>
                    <div style={{ fontSize: "0.65rem", color: "#6B7280" }}>disputes</div>
                  </div>
                </div>

                {/* Shield badge */}
                <ShieldCheck size={18} style={{ color: i === 0 ? "#A78BFA" : "#374151", flexShrink: 0 }} />
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link to="/register"
              className="btn-press inline-flex items-center gap-2 font-medium px-7 py-3.5 rounded-full text-white"
              style={{ background: "linear-gradient(135deg, #6366F1 0%, #A78BFA 100%)", fontSize: "0.95rem" }}>
              List Your Property with a Verified Agent <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
